import {Fragment, useRef, useState} from "react";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import {Box} from "@mui/system";
import {darken, Divider, lighten} from "@mui/material";
import _ from "lodash";

export default function BasicSplitButton ({ options = [], type = "submit", label, handleButtonAction, handleMenuAction, disabled, sx, buttonBordered, useBorderColor='#0C2728', useDark }) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const handleMenuItemClick = (selectedOption) => {
        handleMenuAction(selectedOption)
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const borderStyle = {
        color: useBorderColor,
        borderColor: useBorderColor,
        ':hover': {
            backgroundColor: '#ffffff',
            color: darken(useBorderColor, 0.9),
            borderColor: darken(useBorderColor, 0.9)
        },
        ':disabled': {
            backgroundColor: '#ffffff',
            color: lighten(useBorderColor, 0.55),
            borderColor: lighten(useBorderColor, 0.55)
        },
        ':active': {
            color: useBorderColor
        },
    }

    return (
        <Fragment>
            <ButtonGroup
                sx={buttonBordered ? styles.buttonGroupBordered : styles.buttonGroup}
                variant="contained"
                ref={anchorRef}
                aria-label="message action group with a nested menu">
                <Button
                    sx={buttonBordered ?
                        {
                            ...styles.buttonBordered, ...sx,
                            ...borderStyle
                        }
                        : (useDark ? styles.buttonColoredDark : {...styles.buttonColored, ...sx})}
                    type={type}
                    onClick={handleButtonAction}
                    variant="contained"
                    disableElevation
                    disabled={disabled} >
                    {label}
                </Button>

                <Button
                    sx={buttonBordered ?
                        {
                            ...styles.buttonSmallBordered, ...sx,
                            ...borderStyle
                        }
                        : (useDark ? styles.buttonSmallColoredDark : {...styles.buttonSmallColored, ...sx})}
                    size="small"
                    aria-controls={open ? 'split-button-menu' : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-label="message action menu"
                    aria-haspopup="menu"
                    disabled={disabled}
                    onClick={handleToggle}>
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Popper
                sx={{ zIndex: 1 }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal>
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom'}}>
                        <Paper sx={{border: "solid 1px #dee2e0"}}>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="message-action-menu" autoFocusItem>
                                    {options.map((option, index) => (
                                        <Box key={index}>
                                            <MenuItem
                                                sx={styles.menuItem}
                                                onClick={() => handleMenuItemClick(option)}>
                                                {_.get(option, 'name')}
                                            </MenuItem>
                                            {index < options.length - 1 && <Divider />}
                                        </Box>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </Fragment>
    );
}

const styles = {
    menuItem: {
        height: '25px',
        padding: '0 16px',
        lineHeight: '1rem',
        fontSize: '0.875rem',
    },
    buttonGroup: {
        borderRadius: '50px',
        '& .MuiButton-root': {
            borderColor: '#ffffff',
            '&:hover': {
                borderColor: '#ffffff',
            },
            '&:focus': {
                outline: 'none',
                boxShadow: 'none',
            },
        },
    },
    buttonGroupBordered: {
        borderRadius: '50px',
        '& .MuiButton-root': {
            borderColor: '#07857C',
            '&:hover': {
                borderColor: '#07857C',
            },
            '&:focus': {
                outline: 'none',
                boxShadow: 'none',
            },
        },
    },
    buttonColored: {
        width: '100%',
        fontSize: '16px',
        padding: '11px 24px',
        color: '#fff',
        fontFamily: 'LarsseitMedium',
        lineHeight: 1,
        textTransform: 'none',
        borderRadius: '50px',
        backgroundColor: '#07857C',
        ':hover': {
            backgroundColor: '#34756f',
            color: 'white',
        },
        ':disabled': {
            color: '#fff',
            backgroundColor: '#368C8680'
        },
        ':focus': {
            outline: '0'
        }
    },
    buttonSmallColored: {
        fontSize: '16px',
        color: '#fff',
        fontFamily: 'LarsseitMedium',
        lineHeight: 1,
        textTransform: 'none',
        borderRadius: '50px',
        backgroundColor: '#07857C',
        ':hover': {
            backgroundColor: '#34756f',
            color: 'white',
        },
        ':disabled': {
            color: '#fff',
            backgroundColor: '#368C8680'
        },
        ':focus': {
            outline: '0'
        }
    },
    buttonColoredDark: {
        width: '100%',
        fontSize: '16px',
        padding: '11px 24px',
        color: '#fff',
        fontFamily: 'LarsseitMedium',
        lineHeight: 1,
        textTransform: 'none',
        borderRadius: '50px',
        backgroundColor: '#144042',
        ':hover': {
            backgroundColor: '#103536',
            color: 'white',
        },
        ':disabled': {
            color: '#fff',
            backgroundColor: '#A1B3B3'
        },
        ':focus': {
            outline: '0'
        }
    },
    buttonSmallColoredDark: {
        fontSize: '16px',
        color: '#fff',
        fontFamily: 'LarsseitMedium',
        lineHeight: 1,
        textTransform: 'none',
        borderRadius: '50px',
        backgroundColor: '#144042',
        ':hover': {
            backgroundColor: '#103536',
            color: 'white',
        },
        ':disabled': {
            color: '#fff',
            backgroundColor: '#A1B3B3'
        },
        ':focus': {
            outline: '0'
        }
    },
    buttonBordered: {
        width: '100%',
        fontSize: '16px',
        padding: '9px 25px',
        color: '#368C86',
        fontFamily: 'LarsseitMedium',
        lineHeight: 1,
        textTransform: 'none',
        borderRadius: '50px',
        backgroundColor: '#ffffff',
        border: '1.5px solid #368C86',
        textDecoration: 'none',
        ':hover': {
            backgroundColor: '#ffffff',
            color: '#368C86',
        },
        ':disabled': {
            color: '#368C8680',
            backgroundColor: '#f5f5f5',
            border: '1px solid #368C8680'
        },
        ':focus': {
            outline: '0'
        }
    },
    buttonSmallBordered: {
        fontSize: '16px',
        color: '#368C86',
        fontFamily: 'LarsseitMedium',
        lineHeight: 1,
        textTransform: 'none',
        borderRadius: '50px',
        backgroundColor: '#ffffff',
        border: '1.5px solid #368C86',
        textDecoration: 'none',
        ':hover': {
            backgroundColor: '#ffffff',
            color: '#368C86',
        },
        ':disabled': {
            color: '#368C8680',
            backgroundColor: '#f5f5f5',
            border: '1px solid #368C8680'
        },
        ':focus': {
            outline: '0'
        }
    },
}
